import { useEffect, useState, useMemo } from "react"

interface ContentHtmlType {
    description: string;
    ingredients: string;
    indications: string;
    contraindications: string;
    dosage: string;
    drug_interactions: string;
    storage: string;
    overdose: string;
}

interface InformationProps {
    contentHtml: ContentHtmlType;
}

const SECTION_CONFIG = [
    { key: 'description', label: 'Thông tin chung' },
    { key: 'ingredients', label: 'Chi tiết thành phần' },
    { key: 'indications', label: 'Chỉ định' },
    { key: 'contraindications', label: 'Chống chỉ định' },
    { key: 'dosage', label: 'Liều lượng - Cách dùng' },
    { key: 'drug_interactions', label: 'Tương tác thuốc' },
    { key: 'storage', label: 'Bảo quản' },
    { key: 'overdose', label: 'Quá liều' },
] as const;

export default function Information({
    contentHtml
}: InformationProps) {

    const [activeSection, setActiveSection] = useState("description")
    const HEADER_OFFSET = 144

    // 1. Lọc ra các section CÓ DỮ LIỆU để hiển thị
    const displaySections = useMemo(() => {
        return SECTION_CONFIG.filter(section => {
            const content = contentHtml[section.key as keyof ContentHtmlType];
            return content && content.trim() !== "";
        });
    }, [contentHtml]);

    // 2. Xử lý Click Menu
    const handleClick = (id: string) => {
        const el = document.getElementById(id)
        if (!el) return

        setActiveSection(id)
        const y = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET

        window.scrollTo({
            top: y,
            behavior: "smooth",
        })
    }

    // 3. Xử lý Scroll Spy (Tự động active menu khi cuộn)
    useEffect(() => {
        const handler = () => {
            if (displaySections.length === 0) return;

            const positions = displaySections.map(s => {
                const el = document.getElementById(s.key)
                if (!el) return { id: s.key, distance: Infinity }

                const top = el.getBoundingClientRect().top - HEADER_OFFSET

                return { id: s.key, distance: Math.abs(top) }
            })

            positions.sort((a, b) => a.distance - b.distance)

            if (positions.length > 0) {
                setActiveSection(positions[0].id)
            }
        }

        window.addEventListener("scroll", handler)
        return () => window.removeEventListener("scroll", handler)
    }, [displaySections])

    // Nếu không có nội dung gì thì ẩn luôn component
    if (displaySections.length === 0) return null;

    return (
        <div>
            <section id="product-information" className="grid grid-cols-3 gap-5 p-5 rounded-[20px] bg-white mb-5">

                {/* === LEFT MENU (STICKY) === */}
                <div className="col-span-1 relative hidden-1199">
                    <ul className="flex flex-col gap-3 list-none pl-0 sticky top-[144px]">
                        {displaySections.map(item => (
                            <li
                                key={item.key}
                                onClick={() => handleClick(item.key)}
                                className={`w-full rounded-[8px] flex items-center py-5 px-3 font-[ggsm] cursor-pointer transition-colors duration-200
                                    ${activeSection === item.key
                                        ? "bg-[var(--primary-color)] text-white"
                                        : "bg-[#FAFAFA] text-[var(--primary-color)] hover:bg-[#D1E7BF]"
                                    }`}
                            >
                                {item.label}
                            </li>
                        ))}
                    </ul>
                </div>

                {/* === RIGHT CONTENT === */}
                <div className="col-span-2 relative max-w-full-1199">
                    <div className="flex flex-col gap-2 overflow-hidden transition-all duration-500">

                        {displaySections.map(item => (
                            <div
                                key={item.key}
                                id={item.key}
                                className="flex flex-col gap-2 mb-6 scroll-mt-[150px]"
                            >
                                <h3 className="text-[18px] text-[var(--primary-color)] font-[ggsm] border-b pb-2">
                                    {item.label}
                                </h3>

                                {/* Render HTML content */}
                                <div
                                    className="[&_*]:m-[revert] [&_iframe]:!max-w-full [&_ul]:[all:revert] [&_ol]:[all:revert] [&_p]:[all:revert] [overflow-wrap:anywhere] [&_table]:inline-block jodit-wysiwyg text-[#333] leading-relaxed"
                                    dangerouslySetInnerHTML={{
                                        __html: contentHtml[item.key as keyof ContentHtmlType]
                                    }}
                                />
                            </div>
                        ))}

                    </div>
                </div>
            </section>
        </div>
    )
}